import React from "react";
import { Controller } from "react-hook-form";
import styled from "styled-components";

import ZipCode, { REGION } from "../../../../../utils/zipcode";

import { FieldWrapper, validationRules, TextInputField, SingleSelectField } from '..'
import { TextInput } from "../../../ui-elements/TextInput";
import { Button } from "../../../ui-elements/Button";
import { AddressWithPostalCodeFieldProps } from "./AddressWithPostalCodeField.types";
import { styleTheme } from "../../../../style/theme";

export const Component: React.FC<AddressWithPostalCodeFieldProps & {className: string }> = (props) => {
  const {
    control,
    names = {
      postalCode: 'postalCode',
      prefecture: 'prefecture',
      city: 'city',
      houseNumber: 'houseNumber',
      buildingName: 'buildingName',
    },
    placeholders,
    required,
    disabled,
    setValue,
    register,
    errors
  } = props

  const handleSearch = (postalCode: string) => {
    ZipCode.search(postalCode).then((addr) => {
      setValue(names.postalCode, addr.zip)
      setValue(names.prefecture, addr.region)
      setValue(names.city, addr.locality)
      setValue(names.houseNumber, addr.street + addr.extended)
    })
  }

  return (
    <FieldWrapper
      label={props.label}
      isRequired={props.required}
      column={props.column}
      labelClassName={props.labelClassName}
      fieldClassName={props.fieldClassName}
      error={!!(errors[names.postalCode] || errors[names.prefecture] || errors[names.city] || errors[names.houseNumber] || errors[names.buildingName]) ? {message: ''} : undefined}
    >
      <FieldWrapper
        isRequired={props.required}
        column
        error={errors[names.postalCode]}
      >
        <div className={`${props.className} postal-code-wrapper`}>
          <Controller
            control={control}
            name={names.postalCode}
            render={({ field }) => (
              <>
                <TextInput
                  value={field.value?.split('-')[0] || ''}
                  onChange={e => {
                    const secondHalf = field.value?.split('-')[1] || ''
                    const fullPostalCode = (e.target.value || secondHalf) ? `${e.target.value}-${secondHalf}` : '';
                    setValue(names.postalCode, fullPostalCode)
                  }}
                  placeholder={placeholders?.postalCodeFirstHalf || '000'}
                  maxLength={3}
                  className={`${props.className} postal-code-input ${errors[names.postalCode] ? 'error' : ''}`}
                  disabled={disabled}
                />
                <span>-</span>
                <TextInput
                  value={field.value?.split('-')[1] || ''}
                  onChange={e => {
                    const firstHalf = field.value?.split('-')[0] || ''
                    const fullPostalCode = (firstHalf || e.target.value) ? `${firstHalf}-${e.target.value}` : '';
                    setValue(names.postalCode, fullPostalCode)
                  }}
                  placeholder={placeholders?.postalCodeSecondHalf || '0000'}
                  maxLength={4}
                  className={`${props.className} postal-code-input ${errors[names.postalCode] ? 'error' : ''}`}
                  disabled={disabled}
                />
                <Button
                  onClick={() => handleSearch(field.value)}
                  color={props.color ?? styleTheme.colors.teal.base}
                  type="button"
                  invert
                >
                  住所検索
                </Button>
              </>
            )}
            rules={{
              required: required && validationRules.required('郵便番号'),
              pattern: validationRules.pattern.postalCode()
            }}
          />
        </div>
      </FieldWrapper>
      <SingleSelectField
        label="都道府県"
        column
        name={names.prefecture}
        control={control}
        required={required}
        error={errors[names.prefecture]}
        placeholder={placeholders?.prefecture || '都道府県を選択してください'}
        options={REGION.filter(prefecture => !!prefecture).map(prefecture => ({value: prefecture, label: prefecture}))}
        disabled={disabled}
        fieldClassName={`${props.className} prefecture-select-field`}
        labelClassName={`${props.className} item-labels prefecture`}
      />
      <TextInputField
        label="市区町村"
        column
        name={names.city}
        register={register}
        required={required}
        error={errors[names.city]}
        placeholder={placeholders?.city || ''}
        disabled={disabled}
        labelClassName={`${props.className} item-labels city`}
      />
      <TextInputField
        label="町域・番地"
        column
        name={names.houseNumber}
        register={register}
        required={required}
        error={errors[names.houseNumber]}
        placeholder={placeholders?.houseNumber || ''}
        disabled={disabled}
        labelClassName={`${props.className} item-labels houseNumber`}
      />
      <TextInputField
        label="建物名・部屋番号"
        column
        name={names.buildingName}
        register={register}
        error={errors[names.buildingName]}
        placeholder={placeholders?.buildingName || ''}
        disabled={disabled}
        labelClassName={`${props.className} item-labels buildingName`}
      />
    </FieldWrapper>
  );
};

export const AddressWithPostalCodeField: React.FC<AddressWithPostalCodeFieldProps> = styled(Component)`
  &.postal-code-input {
    max-width: 100px;
    &.error {
      border: 1px solid ${styleTheme.colors.pink.base};
    }
  }

  &.postal-code-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;

    > span {
      font-size: 16px;
    }

    > button {
      width: 98px;
      height: 48px;
    }
  }

  &.prefecture-select-field {
    max-width: 360px;
  }

  &.item-labels {
    &.prefecture, &.city, &.houseNumber, &.buildingName {
      font-size: 12px;
      font-weight: normal;
    }
  }
`;
